import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const BookingContext = createContext(null);
const STORAGE_KEY = 'homevilas.bookingId';
const DRAFT_KEY = 'homevilas.bookingDraft';

const readStoredId = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (_) {
    return '';
  }
};

const readStoredDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
};

export const BookingProvider = ({ children }) => {
  const [bookingId, setBookingIdState] = useState(readStoredId);
  const [draft, setDraftState] = useState(readStoredDraft);

  const setBookingId = useCallback((value) => {
    const next = value ? String(value) : '';
    setBookingIdState(next);
    try {
      if (next) {
        localStorage.setItem(STORAGE_KEY, next);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) {
      // ignore storage failures
    }
  }, []);

  const setDraft = useCallback((updater) => {
    setDraftState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater || null;
      try {
        if (next) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
        } else {
          localStorage.removeItem(DRAFT_KEY);
        }
      } catch (_) {
        // ignore storage failures
      }
      return next;
    });
  }, []);

  const clearBooking = useCallback(() => {
    setBookingIdState('');
    setDraftState(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DRAFT_KEY);
    } catch (_) {
      // ignore storage failures
    }
  }, []);

  const value = useMemo(
    () => ({
      bookingId,
      draft,
      setBookingId,
      setDraft,
      clearBooking,
    }),
    [bookingId, draft, setBookingId, setDraft, clearBooking]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBookingContext = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingContext must be used within BookingProvider');
  }
  return context;
};
